/**
 * product (제품) orval 래퍼
 */
import { customInstance } from "../custom-instance";
import { ProductImageSchema, TotalStockInfoSchema, } from "./default-schemes";
import { PRODUCT_TYPE } from "@erp/shared/types/product.types";
import { validateArrayWithSchema, validateWithSchema, } from "@erp/shared/utils/https/zod-validation";
import { z } from "zod";
// ── 스키마 & 타입 (Product) ──
export const GetProductsResponseSchema = z
    .object({
    id: z.coerce.number(),
    productCode: z.string(),
    type: z.nativeEnum(PRODUCT_TYPE),
    classificationId: z.coerce.number().nullable(),
    classification: z.string().nullable(),
    manufacturerId: z.coerce.number().nullable(),
    manufacturer: z.string().nullable(),
    model: z.string().nullable(),
    name: z.string(),
    spec: z.string().nullable(),
    directlyWritten: z.union([z.literal(0), z.literal(1)]).nullable(),
    quantityUnit: z.string().nullable(),
    images: z.array(ProductImageSchema).optional(),
})
    .strict();
// ── 스키마 & 타입 (StockProducts) ──
export const GetStockProductItemSchema = z
    .object({
    ...GetProductsResponseSchema.shape,
    spotId: z.coerce.number(),
    spot: z.string(),
    stockNum: z.coerce.number(),
    latestStockDate: z.string(),
    unitPrice: z.coerce.number().optional(),
    maxUnitPrice: z.coerce.number().optional(),
    minUnitPrice: z.coerce.number().optional(),
    totalPrice: z.coerce.number().optional(),
    conditions: z.enum(["G", "B"]).nullable(),
})
    .strict();
export const GetStockProduuctParamsSchema = z
    .object({
    ...GetStockProductItemSchema.partial().shape,
    conditions: z.enum(["G", "B"]).optional(),
    date: z.string().optional(),
    hasStock: z.boolean().optional(),
})
    .strict();
export const GetStockProductResponseSchema = z
    .object({
    items: z.array(GetStockProductItemSchema),
    ...TotalStockInfoSchema.shape,
})
    .strict();
// ── 스키마 & 타입 (EigenStockProducts) ──
export const GetEigenStockProductItemSchema = z
    .object({
    ...GetStockProductItemSchema.shape,
    spot: z.string(),
    spotId: z.number(),
})
    .strict();
export const GetEigenStockProductResponseSchema = z
    .object({
    items: z.array(GetEigenStockProductItemSchema),
    ...TotalStockInfoSchema.shape,
})
    .strict();
export const GetEigenStockProductParamsSchema = z
    .object({
    ...GetEigenStockProductItemSchema.partial().shape,
    conditions: z.enum(["G", "B"]).optional(),
    date: z.string().optional(),
    hasStock: z.boolean().optional(),
    productId: z.number().optional(),
    spotId: z.number().optional(),
})
    .strict();
// ── 스키마 & 타입 (StockBySpot) ──
const ManufacturerSchema = z
    .object({ id: z.number(), name: z.string() })
    .strict();
const ClassificationSchema = z
    .object({ id: z.number(), name: z.string() })
    .strict();
const ProductSchema = z
    .object({
    id: z.number(),
    type: z.string().nullable(),
    productCode: z.string(),
    name: z.string().nullable(),
    model: z.string().nullable(),
    spec: z.string().nullable(),
    quantityUnit: z.string().nullable(),
    manufacturer: ManufacturerSchema,
    classification: ClassificationSchema,
    images: z.array(ProductImageSchema.nullable()),
})
    .strict();
const StockItemSchema = z
    .object({
    id: z.number(),
    productId: z.number(),
    conditions: z.string().nullable(),
    stockNum: z.coerce.number(),
})
    .strict();
const LastStockCheckImageSchema = z
    .object({
    id: z.number(),
    stockCheckId: z.number(),
    imageUrl: z.string(),
})
    .strict();
const LastStockCheckSchema = z
    .object({
    id: z.number(),
    productId: z.number(),
    creater: z.object({ id: z.number(), name: z.string() }).strict(),
    images: z.array(LastStockCheckImageSchema),
    createdTime: z.coerce.string(),
    isOk: z.boolean(),
    memo: z.string().optional().nullable(),
})
    .strict();
export const StockBySpotItemSchema = z
    .object({
    product: ProductSchema,
    stocks: z.array(StockItemSchema),
    laststockCheck: z.array(LastStockCheckSchema.nullable()),
})
    .strict();
export const GetStockBySpotResponseSchema = z
    .object({ items: z.array(StockBySpotItemSchema) })
    .strict();
export const GetBOMListResponseSchema = z.object({
    ...GetProductsResponseSchema.shape,
    quantity: z.number(),
    unitPrice: z.number().optional(),
    children: z.array(z.lazy(() => GetBOMListResponseSchema)),
});
// ── URL 매핑 ──
const PRODUCT_URLS = {
    commercial: {
        products: "/product/commercial",
        upsert: "/product/commercial/upsert",
        stockProducts: "/product/commercial/stock",
        eigenStockProducts: "/product/commercial/stock/eigen",
        assemble: "/product-assembly/commercial",
    },
    research: {
        products: "/product/research",
        stockProducts: "/product/research/stock",
        eigenStockProducts: "/product/research/stock/eigen",
        assemble: "/product-assembly/research",
    },
};
// ── API 함수 (Product) ──
function createGetProducts(type) {
    return async (params) => {
        const data = await customInstance({
            url: PRODUCT_URLS[type].products,
            method: "GET",
            params,
        });
        return validateArrayWithSchema(GetProductsResponseSchema, data);
    };
}
export const getCommercialProducts = createGetProducts("commercial");
export const getResearchProducts = createGetProducts("research");
function createPostProduct(type) {
    return async (body) => {
        return customInstance({
            url: PRODUCT_URLS[type].products,
            method: "POST",
            headers: { "Content-Type": "application/json" },
            data: body,
        });
    };
}
export const postCommercialProduct = createPostProduct("commercial");
export const postResearchProduct = createPostProduct("research");
function createPatchProduct(type) {
    return async (id, body) => {
        return customInstance({
            url: `${PRODUCT_URLS[type].products}/${id}`,
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            data: body,
        });
    };
}
export const patchCommercialProduct = createPatchProduct("commercial");
export const patchResearchProduct = createPatchProduct("research");
function createDeleteProduct(type) {
    return async (id) => {
        return customInstance({
            url: `${PRODUCT_URLS[type].products}/${id}`,
            method: "DELETE",
        });
    };
}
export const deleteCommercialProduct = createDeleteProduct("commercial");
export const deleteResearchProduct = createDeleteProduct("research");
// ── API 함수 (StockProducts) ──
function createGetStockProducts(type) {
    return async (params) => {
        const data = await customInstance({
            url: PRODUCT_URLS[type].stockProducts,
            method: "GET",
            params,
        });
        return validateWithSchema(GetStockProductResponseSchema, data);
    };
}
export const getCommercialStockProducts = createGetStockProducts("commercial");
export const getResearchStockProducts = createGetStockProducts("research");
// ── API 함수 (EigenStockProducts) ──
function createGetEigenStockProducts(type) {
    return async (params) => {
        const data = await customInstance({
            url: PRODUCT_URLS[type].eigenStockProducts,
            method: "GET",
            params: { ...params, noInitial: true },
        });
        return validateWithSchema(GetEigenStockProductResponseSchema, data);
    };
}
export const getCommercialEigenStockProducts = createGetEigenStockProducts("commercial");
export const getResearchEigenStockProducts = createGetEigenStockProducts("research");
// ── API 함수 (StockBySpot / ProductCheck) ──
export const getStockBySpot = async (spotId) => {
    const data = await customInstance({
        url: `/stock-check/${spotId}`,
        method: "GET",
    });
    validateWithSchema(GetStockBySpotResponseSchema, data);
    return data;
};
export const upsertCommercialProduct = async (data) => {
    return customInstance({
        url: PRODUCT_URLS.commercial.upsert,
        method: "POST",
        headers: { "Content-Type": "application/json" },
        data,
    });
};
export async function postProductCheck(spotId, body) {
    return customInstance({
        url: `/stock-check/${spotId}`,
        method: "POST",
        headers: { "Content-Type": "application/json" },
        data: body,
    });
}
// ── API 함수 (BOM) ──
function createGetBOMList(type) {
    return async (productId) => {
        const data = await customInstance({
            url: `${PRODUCT_URLS[type].assemble}/product/${productId}`,
            method: "GET",
        });
        return validateArrayWithSchema(GetBOMListResponseSchema, data);
    };
}
export const getCommercialBOMList = createGetBOMList("commercial");
export const getResearchBOMList = createGetBOMList("research");
function createPostBOMList(type) {
    return async (body) => {
        return customInstance({
            url: `${PRODUCT_URLS[type].assemble}/many`,
            method: "POST",
            headers: { "Content-Type": "application/json" },
            data: body,
        });
    };
}
export const postCommercialBOMList = createPostBOMList("commercial");
export const postResearchBOMList = createPostBOMList("research");
function createPatchBOMList(type) {
    return async (body) => {
        return customInstance({
            url: `${PRODUCT_URLS[type].assemble}/many`,
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            data: body,
        });
    };
}
export const patchCommercialBOMList = createPatchBOMList("commercial");
export const patchResearchBOMList = createPatchBOMList("research");
// ── SDK 객체 ──
export const productApi = {
    getCommercialProducts,
    getResearchProducts,
    postCommercialProduct,
    postResearchProduct,
    patchCommercialProduct,
    patchResearchProduct,
    deleteCommercialProduct,
    deleteResearchProduct,
    getCommercialStockProducts,
    getResearchStockProducts,
    getCommercialEigenStockProducts,
    getResearchEigenStockProducts,
    getStockBySpot,
    upsertCommercialProduct,
    postProductCheck,
    getCommercialBOMList,
    getResearchBOMList,
    postCommercialBOMList,
    postResearchBOMList,
    patchCommercialBOMList,
    patchResearchBOMList,
};
